import ItemService from "../services/supplier_appCommonService";

/**
 * Get forms filtered by type (COMM or TECH) - uses API with rowFilter
 * @param {string} formType - "COMM" or "TECH"
 * @returns {Array} Filtered forms
 */
export const getFormsByType = async (formType) => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await ItemService.generateQuery({
      queryName: "getQuestionnaireNamesList",
      rowFilter: { type: formType },
      timeZone
    });
    if (response?.data) {
      // API returns 'type' field, normalize to 'formType' for frontend compatibility
      // Also preserve the code field (e.g., QUESTN0001) for getQuestionnaireDetail API
      return response.data.map(f => ({
        ...f,
        id: f._id || f.documentNumber || f.questionnaireNr,
        code: f.code || f.documentNumber || f.questionnaireNr,
        formType: f.type,
        name: f.name
      }));
    }
    return [];
  } catch (error) {
    console.error('Error loading forms by type:', error);
    return [];
  }
};

/**
 * Add a new questionnaire form via API
 * @param {Object} form - Form object to save
 * @returns {Object} Response from API
 */
export const addQuestionnaire = async (form) => {
  try {
    const payload = {
      type: form.formType,
      name: form.name,
      sections: form.sections,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    const response = await ItemService.addQuestionnaire(payload);
    return response;
  } catch (error) {
    console.error('Error adding questionnaire:', error);
    throw error;
  }
};

/**
 * Update an existing questionnaire form via API
 * @param {string} documentNumber - Document number of the form (actually _id)
 * @param {Object} form - Updated form data
 * @returns {Object} Response from API
 */
export const updateQuestionnaire = async (documentNumber, form) => {
  try {
    const payload = {
      _id: documentNumber,
      name: form.name,
      sections: form.sections,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    const response = await ItemService.updateQuestionnaire(payload);
    return response;
  } catch (error) {
    console.error('Error updating questionnaire:', error);
    throw error;
  }
};

/**
 * Delete a questionnaire form via API
 * @param {string} documentNumber - Document number of the form to delete (e.g., QUESTN0001)
 * @returns {Object} Response from API
 */
export const deleteQuestionnaire = async (documentNumber) => {
  try {
    const response = await ItemService.deleteQuestionnaire({
      documentNumber: documentNumber,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    return response;
  } catch (error) {
    console.error('Error deleting questionnaire:', error);
    throw error;
  }
};

/**
 * Get questionnaire detail by document number
 * @param {string} documentNumber - Document number (e.g., QUESTN0001)
 * @returns {Object} Form detail with sections
 */
export const getQuestionnaireDetail = async (documentNumber) => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await ItemService.generateQuery({
      queryName: "getQuestionnaireTemplateDetail",
      documentNumber: documentNumber,
      timeZone
    });
    // API returns data as an array - return the first element
    const data = response?.data;
    return Array.isArray(data) && data.length > 0 ? data[0] : data;
  } catch (error) {
    console.error('Error getting questionnaire detail:', error);
    throw error;
  }
};

/**
 * Combine all sections' questions into a flat array with sequential serialNo
 * @param {Array} sections - Array of section objects with questions
 * @returns {Array} Flat array of questions with updated serialNo and componentId
 */
export const combineSections = (sections) => {
  let serialNo = 1;
  const combined = [];

  sections.forEach(section => {
    if (section.questions && section.questions.length > 0) {
      section.questions.forEach(question => {
        combined.push({
          ...question,
          componentId: `question_${serialNo}`,
          serialNo: serialNo
        });
        serialNo++;
      });
    }
  });

  return combined;
};

/**
 * Initialize default forms - now just a no-op since we load from API
 * Kept for backward compatibility
 */
export const initializeDefaultForms = () => {
  // No-op: forms are loaded from API now
  return [];
};